import Ellipsis from "components/Ellipsis";
import ResizableContainer from "components/ResizableContainer";
import { useSorting } from "components/SelectDictTable/useSorting";
import { sortingKeys, sortingModes } from "constants/dictionary";
import { useColumnsResizing } from "hooks/generic/useColumnsResizing";
import {
  ScrollbarPlaceholder,
  useScrollbarPlaceholderWidth,
} from "hooks/generic/useScrollbarPlaceholderWidth";
import { ReactComponent as ArrowDown } from "icons/dictionary/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "icons/dictionary/ArrowUp.svg";
import { ReactComponent as Tick } from "icons/tick-grey.svg";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { formatDictionaryDate } from "utils/dictionary";
import handleKeyboardClick from "utils/handleKeyboardClick";

const SelectDictTable = ({
  dictionaries,
  selectedDictionaryId,
  newlyCreatedDictionaryId,
  headerConfig,
  initColumnWidths,
  minimalColumnWidths,
  onDictionarySelect,
  onEnter,
  onDictionaryPick,
  resetNewlyCreatedDictionaryId,
}) => {
  const tableHeaderRef = useRef();
  const tableBodyRef = useRef();

  const [hoveredDictionaryId, setHoveredDictionaryId] = useState(null);
  const [sortedDictionaries, setSortedDictionaries] = useState(dictionaries);
  const [sortedBy, setSortedBy] = useState(sortingKeys.name);
  const [sortingMode, setSortingMode] = useState(sortingModes.ascending);

  useEffect(() => {
    setSortedDictionaries(dictionaries);
  }, [dictionaries]);

  useSorting(sortedBy, sortingMode, dictionaries, setSortedDictionaries);

  const { columnWidths, setColumnWidths, onColumnResize } = useColumnsResizing(
    initColumnWidths,
    minimalColumnWidths,
    "",
    tableHeaderRef
  );

  const { scrollbarPlaceholderWidth } = useScrollbarPlaceholderWidth(
    tableBodyRef,
    setColumnWidths
  );

  const onSortClick = (sortBy) => {
    if (sortBy === sortedBy) {
      if (sortingMode === sortingModes.ascending) {
        setSortingMode(sortingModes.descending);
      } else {
        setSortingMode(sortingModes.ascending);
      }
    } else {
      setSortedBy(sortBy);
      setSortingMode(sortingModes.ascending);
    }
  };

  const handleClick = (id) => {
    onDictionarySelect(id);
  };

  const handleKeyDown = ({ key }, id) => {
    if (key === "Enter") {
      onEnter(id);
    }
  };

  const handleBodyScroll = ({ target: { scrollLeft } }) => {
    if (tableHeaderRef.current && tableBodyRef.current) {
      tableHeaderRef.current.scrollLeft = scrollLeft;
    }
  };

  const renderSortIcon = (sortBy) => {
    if (sortBy === sortedBy) {
      if (sortingMode === sortingModes.ascending) {
        return <ArrowDown />;
      }
      if (sortingMode === sortingModes.descending) {
        return <ArrowUp />;
      }
    }
    return <ArrowDown />;
  };

  const renderRows = (dictionary) => {
    return headerConfig.map(({ title }, index) => {
      const style = { width: columnWidths[index] };
      switch (title) {
        case `Dictionaries (${dictionaries.length})`:
          return (
            <span style={style}>
              <Ellipsis
                text={dictionary.name}
                tooltip={dictionary.description || null}
              />
            </span>
          );
        case "Created By":
          return (
            <span style={style}>
              <Ellipsis text={dictionary.createdBy || "System"} />
            </span>
          );
        case "Created On":
          return (
            <span style={style}>
              <Ellipsis text={formatDictionaryDate(dictionary.createdOn)} />
            </span>
          );
        case "Last Updated":
          return (
            <span style={style}>
              <Ellipsis text={dictionary.lastUpdated} />
            </span>
          );
        case "In Use":
          return <span style={style}>{dictionary.inUse && <Tick />}</span>;
        default:
          return null;
      }
    });
  };

  return (
    <div className="select_dict_table" data-testid="select-dict-table">
      <div ref={tableHeaderRef} className="select_dict_table__header">
        {headerConfig.map(({ title, sortBy }, index) => (
          <ResizableContainer
            key={title}
            index={index}
            width={columnWidths[index]}
            minWidth={minimalColumnWidths[index]}
            disableResizing={index === headerConfig.length - 1}
            onResize={onColumnResize}
          >
            <span
              className="select_dict_table__header__cell"
              onClick={() => onSortClick(sortBy)}
              onKeyDown={handleKeyboardClick(() => onSortClick(sortBy))}
            >
              <Ellipsis text={title} />
              <button
                type="button"
                className={`sort_button${sortBy === sortedBy ? " active" : ""}`}
                data-testid={sortBy}
              >
                {renderSortIcon(sortBy)}
              </button>
            </span>
          </ResizableContainer>
        ))}
        <ScrollbarPlaceholder width={scrollbarPlaceholderWidth} />
      </div>
      <div
        ref={tableBodyRef}
        className="select_dict_table__body"
        onScroll={handleBodyScroll}
      >
        {sortedDictionaries.map((dictionary) => {
          const handleDoubleClick = () => {
            onDictionaryPick(dictionary.id);
          };

          const scrollToDictionary = (node) => {
            if (node !== null && dictionary.id === newlyCreatedDictionaryId) {
              node.scrollIntoView();
              resetNewlyCreatedDictionaryId();
            }
          };

          return (
            <div
              role="button"
              ref={scrollToDictionary}
              tabIndex={0}
              key={dictionary.id}
              onMouseOut={() => setHoveredDictionaryId(null)}
              onMouseOver={() => setHoveredDictionaryId(dictionary.id)}
              onFocus={() => setHoveredDictionaryId(dictionary.id)}
              onBlur={() => setHoveredDictionaryId(null)}
              onClick={() => handleClick(dictionary.id)}
              onKeyDown={(e) => handleKeyDown(e, dictionary.id)}
              onDoubleClick={() => handleDoubleClick()}
            >
              <span
                className={`${selectedDictionaryId === dictionary.id ? "selected" : ""}${
                  dictionary.id === hoveredDictionaryId &&
                  hoveredDictionaryId !== selectedDictionaryId
                    ? " hovered"
                    : ""
                }`}
              />
              <div className="select_dict_table__body__row">
                {renderRows(dictionary)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

SelectDictTable.propTypes = {
  dictionaries: PropTypes.arrayOf(PropTypes.shape({})),
  selectedDictionaryId: PropTypes.number.isRequired,
  newlyCreatedDictionaryId: PropTypes.number,
  headerConfig: PropTypes.arrayOf(PropTypes.shape({})),
  initColumnWidths: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  minimalColumnWidths: PropTypes.arrayOf(PropTypes.number),
  onDictionarySelect: PropTypes.func,
  onEnter: PropTypes.func,
  onDictionaryPick: PropTypes.func,
  resetNewlyCreatedDictionaryId: PropTypes.func,
};

export default SelectDictTable;
